import { useCallback } from 'react';
import { supabase } from '../lib/supabase';

export function useAgentInteractions() {
  const trackInteraction = useCallback(async (
    agentId: string, 
    interactionType: 'view' | 'analyze' | 'generate_ui' | 'use_ui',
    metadata: Record<string, any> = {}
  ) => {
    try {
      const { error } = await supabase
        .from('agent_interactions')
        .insert({
          agent_id: agentId,
          interaction_type: interactionType,
          metadata
        });

      if (error) {
        console.error('Error tracking interaction:', error);
      }
    } catch (error) {
      console.error('Error tracking interaction:', error);
    }
  }, []);

  const getAgentInteractions = useCallback(async (agentId: string) => {
    try {
      const { data, error } = await supabase
        .from('agent_interactions')
        .select('*')
        .eq('agent_id', agentId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching interactions:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching interactions:', error);
      return [];
    }
  }, []);

  const getPopularAgents = useCallback(async (limit: number = 10) => {
    try {
      const { data, error } = await supabase
        .from('agent_interactions')
        .select('agent_id, agents!inner(*)')
        .eq('interaction_type', 'view')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching popular agents:', error);
        return [];
      }

      // Group by agent and count interactions
      const agentCounts = data?.reduce((acc: any, interaction: any) => {
        const agentId = interaction.agent_id;
        if (!acc[agentId]) {
          acc[agentId] = {
            agent: interaction.agents,
            count: 0
          };
        }
        acc[agentId].count++;
        return acc;
      }, {});

      return Object.values(agentCounts || {})
        .sort((a: any, b: any) => b.count - a.count)
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching popular agents:', error);
      return [];
    }
  }, []);

  return {
    trackInteraction,
    getAgentInteractions,
    getPopularAgents
  };
}